import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import { CustomCircleTickIcon } from '@nesso/ui/icons/custom-circle-tick';
import { cn } from '@nesso/ui/lib/utils';

import { sessionByIdOptions } from '@/entities/session';
import {
    useIsGenerating,
    useIsGenerationAvailable,
    useToggleCompletionOptions,
} from '@/entities/session-note';
import { useConfetti } from '@/shared/lib/use-confetti';
import { PopoverTooltipButton } from '@/shared/ui/common/popover-tooltip-button';

import { useSessionId } from '../../../../../model/use-session-id';
import { MarkDonePopover } from './mark-done-button/mark-done-popover';

export function MarkDoneButton() {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const { triggerConfettiFromEvent } = useConfetti();

    const sessionId = useSessionId();
    const { data: session } = useQuery(sessionByIdOptions(sessionId));
    const noteId = session?.currentNote?.id;
    const noteStatus = session?.currentNote?.status ?? null;
    const doneAt = session?.currentNote?.doneAt ?? null;
    const isMarkedDone = noteStatus === 'DONE';
    const isGenerating = useIsGenerating(sessionId);
    const isGenerationAvailable = useIsGenerationAvailable(sessionId);
    const isDisabled = isGenerating || (isGenerationAvailable && !session?.currentNote);

    const toggleCompletionMutation = useMutation(useToggleCompletionOptions(sessionId));

    const handleToggleCompletion = () => {
        if (!noteId) return;
        const newStatus = noteStatus === 'DONE' ? 'TODO' : 'DONE';
        toggleCompletionMutation.mutate({ id: noteId, status: newStatus });
    };

    const undoMarkDone = () => {
        setPopoverOpen(false);
        handleToggleCompletion();
    };

    const moreConfetti = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        triggerConfettiFromEvent(e);
    };

    const handleMarkDone = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (!isMarkedDone) {
            triggerConfettiFromEvent(e);
            handleToggleCompletion();
        } else {
            setPopoverOpen((prev) => !prev);
        }
    };

    return (
        <PopoverTooltipButton
            open={popoverOpen}
            onOpenChange={(open) => {
                if (isMarkedDone) {
                    setPopoverOpen(open);
                }
            }}
            popoverProps={{ modal: true }}
            popoverContent={
                <MarkDonePopover
                    markedDoneAt={doneAt ?? null}
                    onUndo={undoMarkDone}
                    onMoreConfetti={moreConfetti}
                />
            }
            popoverContentProps={{
                className: 'w-[19rem] p-2',
                align: 'end',
            }}
            buttonProps={{
                variant: isMarkedDone ? 'ghost' : 'default',
                size: 'default',
                disabled: isDisabled,
                onClick: (e) => {
                    handleMarkDone(e);
                },
                className: cn('text-sm font-medium', popoverOpen && 'bg-accent/50'),
            }}
        >
            {isMarkedDone && <CustomCircleTickIcon className="text-toast-success mr-2" />}
            {isMarkedDone ? 'Marked Done' : 'Mark Done'}
        </PopoverTooltipButton>
    );
}
